/**
 * The homepage's one reveal: a section's heading arrives, then its content
 * staggers in behind it, once, in reading order.
 *
 * Declarative, because six sections need the identical behaviour and none of
 * them needs to publish class names to get it. A section opts in from its
 * Liquid:
 *
 *   <section class="pag-trust-badges" data-pag-reveal="pending">
 *     <div data-pag-reveal-head>…</div>
 *     <div data-pag-reveal-group>…</div>
 *   </section>
 *
 *   data-pag-reveal        marks the section and carries the state machine
 *   data-pag-reveal-speed  optional playback multiplier for this section only,
 *                          1 = the shared timing, 1.3 = 30% faster. Defaults to
 *                          1, so every other section is untouched.
 *   data-pag-reveal-head   leads: one fade-and-lift, may appear more than once
 *   data-pag-reveal-group  staggers its DIRECT CHILDREN
 *   data-pag-reveal-effect="fan"  on a group: items start bunched toward the
 *                          group's centre, tilted and small, and fan out into place
 *   data-pag-reveal-items  flag: the items are marked inside rather than being
 *                          the direct children, for when a direct child is a
 *                          layout wrapper rather than a thing a reader sees
 *                          (pag-blog-stories: its grid holds a featured card and
 *                          a column of two, but the reader sees three cards)
 *   data-pag-reveal-item   marks one of those items
 *
 * Timing, the scroller, reduced motion and the four-value `data-anim` contract
 * all come from @theme/pag-motion — see that file for why the hold is CSS and
 * why this script is responsible for releasing it.
 */

import { TIMING, motionContext, bootSection, release } from '@theme/pag-motion';

/**
 * The elements a group actually animates.
 *
 * A flag plus marked items rather than a selector string, so the CSS hold in
 * pag-overrides.css can match exactly what this matches. A selector living only
 * in a data attribute would leave the stylesheet unable to hold the right
 * elements, and holding the wrapper instead would hide the items inside it for
 * good — the tween puts its inline opacity on the item, not the wrapper.
 */
function itemsOf(group) {
  const deep = group.hasAttribute('data-pag-reveal-items');
  return Array.from(deep ? group.querySelectorAll('[data-pag-reveal-item]') : group.children);
}

const isFan = (el) => el.closest('[data-pag-reveal-group]')?.dataset.pagRevealEffect === 'fan';

/** Start state for one item of a fan group: pulled toward the row centre, tilted, smaller. */
function fanFrom(item) {
  const group = item.closest('[data-pag-reveal-group]');
  const siblings = itemsOf(group);
  const index = siblings.indexOf(item);
  const mid = (siblings.length - 1) / 2;
  const g = group.getBoundingClientRect();
  const r = item.getBoundingClientRect();
  return {
    opacity: 0,
    x: (g.left + g.width / 2 - (r.left + r.width / 2)) * 0.75,
    rotation: (index - mid) * 4,
    scale: 0.82,
  };
}

function setup(section) {
  const heads = Array.from(section.querySelectorAll('[data-pag-reveal-head]'));
  const groups = Array.from(section.querySelectorAll('[data-pag-reveal-group]'));
  const items = groups.flatMap(itemsOf);

  if (heads.length === 0 && items.length === 0) {
    release(section);
    return undefined;
  }

  // A section may ask its own reveal to run faster (or slower) than the shared
  // vocabulary without disturbing the others. Clamped so a stray value cannot
  // stall the reveal or blur it into a single frame.
  const speed = Math.min(3, Math.max(0.25, parseFloat(section.dataset.pagRevealSpeed) || 1));

  return motionContext(section, ({ scroller, gsap: g, ScrollTrigger }) => {
    const trigger = { scroller, trigger: section, start: TIMING.start, once: true };

    if (heads.length > 0) {
      g.from(heads, {
        opacity: 0,
        y: TIMING.head.travel,
        duration: TIMING.head.duration / speed,
        ease: TIMING.head.ease,
        stagger: TIMING.head.stagger / speed,
        scrollTrigger: trigger,
      });
    }

    // One attribute holds every item, so it cannot be released until the last
    // one has arrived: batch fires once per group of items that crosses the
    // line, not once for the section. Releasing early would pop the rows that
    // have not had their turn into view at full opacity.
    let revealed = 0;

    const batches =
      items.length > 0
        ? ScrollTrigger.batch(items, {
            scroller,
            start: TIMING.start,
            once: true,
            onEnter: (targets) => {
              const done = () => {
                revealed += targets.length;
                if (revealed < items.length) return;
                section.dataset.anim = 'done';
                // Hand transforms back, so anything CSS does to these
                // elements afterwards is not outranked by a leftover matrix.
                g.set(items, { clearProps: 'transform' });
              };
              const fanned = targets.filter(isFan);
              const plain = targets.filter((t) => !fanned.includes(t));
              let pending = (fanned.length > 0 ? 1 : 0) + (plain.length > 0 ? 1 : 0);
              const finish = () => {
                pending -= 1;
                if (pending === 0) done();
              };

              if (plain.length > 0) {
                g.fromTo(
                  plain,
                  { opacity: 0, y: TIMING.item.travel, scale: TIMING.item.scale },
                  {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: TIMING.item.duration / speed,
                    ease: TIMING.item.ease,
                    stagger: { each: TIMING.item.stagger / speed, from: 'start', grid: 'auto' },
                    onComplete: finish,
                  }
                );
              }

              if (fanned.length > 0) {
                // Start states depend on where each item sits, so tween one by one.
                const starts = fanned.map(fanFrom);
                fanned.forEach((el, i) => {
                  g.fromTo(el, starts[i], {
                    opacity: 1,
                    x: 0,
                    rotation: 0,
                    scale: 1,
                    duration: 1.1 / speed,
                    delay: (i * 0.12) / speed,
                    ease: 'power3.out',
                    onComplete: i === fanned.length - 1 ? finish : undefined,
                  });
                });
              }
            },
          })
        : [];

    // A section whose only content is a heading has no batch to finish, so it
    // would otherwise sit at 'running' forever.
    if (items.length === 0) section.dataset.anim = 'done';

    return () => {
      batches.forEach((instance) => instance.kill());
      if (items.length > 0) g.set(items, { clearProps: 'all' });
      if (heads.length > 0) g.set(heads, { clearProps: 'all' });
    };
  });
}

bootSection('[data-pag-reveal]', setup, 'reveal');
